// Espace personnel : mes données (F-105, F-107) et traduction d'aide (F-102).
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { patch, post } from "@core/api";
import { PublicShell } from "@core/layout";
import { date, dateTime } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useSession } from "@core/session";
import { Badge, Button, Card, Empty, ErrorBox, Input, Loading, Modal, Notice, Select, Table, Textarea, fieldError, useToast } from "@core/ui";
import { Mode, PRODUCT_LABEL, useAction } from "./shared";

const PURPOSES: Record<string, string> = {
  lead_assurance: "Mise en relation assurance",
  notice_information: "Lecture d’une notice d’information",
  communication_partenaire: "Communication d’un partenaire",
  autre: "Autre",
};
const REQUEST_STATUS: Record<string, string> = { recue: "Reçue", en_cours: "En cours", traitee: "Traitée", refusee: "Refusée" };
const ACCESS: Record<string, string> = {
  "document.consulte": "a consulté",
  "document.depose": "a déposé",
  "document.efface": "a effacé",
  "document.copie_filigranee": "a produit une copie filigranée de",
};

function Back() {
  return (
    <p className="small">
      <Link to="/moi">Mon espace personnel</Link>
    </p>
  );
}

export function AccountDataPage() {
  const consents = useGet<any>("/api/v1/me/consents");
  const requests = useGet<any>("/api/v1/me/rights-requests");
  const access = useGet<any>("/api/v1/me/document-access");
  const invalidate = useInvalidate();
  const toast = useToast();
  const action = useAction();
  const [request, setRequest] = useState({ kind: "acces", message: "" });
  const withdraw = async (id: string) => {
    const r = await action.run(() => post(`/api/v1/me/consents/${id}/withdraw`));
    if (r) {
      toast("Accord retiré.");
      invalidate("/api/v1/me/consents");
    }
  };
  const sendRequest = async () => {
    const r = await action.run(() => post<any>("/api/v1/me/rights-requests", request));
    if (r) {
      toast(r.transmitted_to?.length ? `Demande transmise à : ${r.transmitted_to.join(", ")}.` : "Demande enregistrée.");
      setRequest({ ...request, message: "" });
      invalidate("/api/v1/me/rights-requests");
    }
  };
  const kinds = (requests.data?.kinds ?? []).filter((k: any) => k.code !== "export" && k.code !== "effacement");
  return (
    <PublicShell wide>
      <div className="stack">
        <Back />
        <h1>Mes données</h1>
        <p className="intro">Exportez vos données, gérez vos accords, suivez vos demandes et voyez qui a consulté vos pièces.</p>
        <div className="grid-2">
          <Card title="Exporter mes données">
            <p className="small">Archive ZIP : vos informations au format JSON et les pièces déposées par vous ou vous concernant, dans leur version d’origine.</p>
            <a className="bv-btn bv-btn-secondaire" href="/api/v1/me/data-export">
              Télécharger mon archive
            </a>
          </Card>
          <Card title="Faire une demande">
            <div className="stack-sm">
              <ErrorBox error={action.error && !fieldError(action.error, "message") ? action.error : null} />
              <Select label="Type de demande" value={request.kind} options={kinds.map((k: any) => ({ value: k.code, label: k.label }))} onChange={(e) => setRequest({ ...request, kind: e.target.value })} />
              <Textarea label="Votre demande" value={request.message} error={fieldError(action.error, "message")} onChange={(e) => setRequest({ ...request, message: e.target.value })} />
              <p className="small muted">Elle est transmise aux bailleurs concernés par vos dossiers, qui disposent d’un mois pour répondre.</p>
              <Button variant="secondaire" busy={action.busy} onClick={sendRequest}>
                Envoyer la demande
              </Button>
            </div>
          </Card>
        </div>
        <Card title="Mes demandes">
          {requests.data?.items?.length ? (
            <Table caption="Demandes">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Demande</th>
                  <th>État</th>
                  <th>Échéance</th>
                </tr>
              </thead>
              <tbody>
                {requests.data.items.map((r: any) => (
                  <tr key={r.id}>
                    <td>{dateTime(r.created_at)}</td>
                    <td>
                      {requests.data.kinds.find((k: any) => k.code === r.kind)?.label ?? r.kind}
                      {r.journal.map((j: any, i: number) => (
                        <span key={i} className="block small muted">
                          {j.detail}
                        </span>
                      ))}
                    </td>
                    <td>{REQUEST_STATUS[r.status] ?? r.status}</td>
                    <td>{date(r.due_on)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Empty title="Aucune demande." />
          )}
        </Card>
        <Card title="Mes accords">
          {consents.data?.items?.length ? (
            <ul className="list-plain">
              {consents.data.items.map((c: any) => (
                <li key={c.id} className="plateforme-accord">
                  <div className="row-between">
                    <strong>
                      {PURPOSES[c.purpose] ?? c.purpose}
                      {c.scope?.produit ? ` · ${PRODUCT_LABEL[c.scope.produit] ?? c.scope.produit}` : ""}
                      {c.scope?.partenaire ? ` · ${c.scope.partenaire}` : ""}
                    </strong>
                    {c.withdrawn_at ? <Badge>Retiré le {date(c.withdrawn_at)}</Badge> : <Badge tone="encre">Donné le {date(c.given_at)}</Badge>}
                  </div>
                  {c.org_name ? <span className="small muted">Espace « {c.org_name} »</span> : null}
                  {c.purpose !== "notice_information" ? (
                    <details>
                      <summary className="small">Texte présenté (version {c.text_version})</summary>
                      <p className="small">{c.text_presented}</p>
                    </details>
                  ) : null}
                  {!c.withdrawn_at && c.purpose !== "notice_information" ? (
                    <p>
                      <Button variant="secondaire" small busy={action.busy} onClick={() => withdraw(c.id)}>
                        Retirer mon accord
                      </Button>
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <Empty title="Aucun accord enregistré." />
          )}
        </Card>
        <Card title="Qui a consulté mes pièces">
          {access.data?.items?.length ? (
            <ul className="list-plain small">
              {access.data.items.map((a: any, i: number) => (
                <li key={i}>
                  {dateTime(a.created_at)} · {a.is_me ? "Vous" : a.actor ?? "Une personne"}
                  {a.org_name && !a.is_me ? ` (espace « ${a.org_name} »)` : ""} {ACCESS[a.action] ?? a.action} « {a.document_title} »
                </li>
              ))}
            </ul>
          ) : (
            <Empty title="Aucun accès enregistré à vos pièces." />
          )}
        </Card>
        <ErasureCard />
      </div>
    </PublicShell>
  );
}

function ErasureCard() {
  const [open, setOpen] = useState(false);
  const preview = useGet<any>(open ? "/api/v1/me/erasure-preview" : null);
  const [form, setForm] = useState({ password: "", confirmation: "" });
  const action = useAction();
  const nav = useNavigate();
  const { refresh } = useSession();
  const erase = async () => {
    const r = await action.run(() => post<any>("/api/v1/me/erasure", form));
    if (r) {
      await refresh();
      nav("/");
    }
  };
  return (
    <Card title="Effacer mon compte">
      <p className="small">Vous verrez d’abord ce qui sera effacé, ce qui doit être conservé et pourquoi.</p>
      <Button variant="danger" onClick={() => setOpen(true)}>
        Préparer l’effacement
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Effacer mon compte"
        footer={
          <>
            <Button variant="discret" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button variant="danger" busy={action.busy} disabled={!!preview.data?.blockers?.length} onClick={erase}>
              Effacer définitivement
            </Button>
          </>
        }
      >
        {preview.isLoading ? <Loading /> : null}
        {preview.data ? (
          <div className="stack-sm">
            {preview.data.blockers.map((b: string) => (
              <Notice key={b} tone="erreur">
                {b}
              </Notice>
            ))}
            <strong>Sera effacé</strong>
            <ul className="plateforme-etapes">
              {preview.data.erased.map((x: string) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            {preview.data.forwarded.length ? (
              <>
                <strong>Sera transmis aux bailleurs concernés</strong>
                <ul className="plateforme-etapes">
                  {preview.data.forwarded.map((x: string) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </>
            ) : null}
            <strong>Sera conservé</strong>
            <ul className="plateforme-etapes">
              {preview.data.kept.map((x: string) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            <ErrorBox error={action.error && !fieldError(action.error, "password") && !fieldError(action.error, "confirmation") ? action.error : null} />
            <Input label="Mot de passe" type="password" autoComplete="current-password" value={form.password} error={fieldError(action.error, "password")} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <Input label="Saisissez EFFACER pour confirmer" value={form.confirmation} error={fieldError(action.error, "confirmation")} onChange={(e) => setForm({ ...form, confirmation: e.target.value })} />
          </div>
        ) : null}
      </Modal>
    </Card>
  );
}

/** /moi/traduction : traduction d'aide d'un texte, d'un message ou d'un document ; préférence de langue. */
export function TranslationPage() {
  const [params] = useSearchParams();
  const { user, refresh } = useSession();
  const toast = useToast();
  const invalidate = useInvalidate();
  const langs = useGet<any>("/api/v1/translation/languages");
  const history = useGet<any>("/api/v1/translations");
  const action = useAction();
  const documentId = params.get("document");
  const messageId = params.get("message");
  const [text, setText] = useState("");
  const [target, setTarget] = useState(user?.locale === "en" ? "en" : "en");
  const [result, setResult] = useState<any>(null);
  const translate = async () => {
    const body = documentId ? { source_type: "document", source_id: documentId, target_lang: target } : messageId ? { source_type: "message", source_id: messageId, target_lang: target } : { source_type: "texte", text, target_lang: target };
    const r = await action.run(() => post<any>("/api/v1/translations", body));
    if (r) {
      setResult(r);
      invalidate("/api/v1/translations");
    }
  };
  const setLocale = async (locale: string) => {
    const r = await action.run(() => patch("/api/v1/me", { locale }));
    if (r) {
      await refresh();
      toast("Préférence de langue enregistrée.");
    }
  };
  return (
    <PublicShell wide>
      <div className="stack">
        <Back />
        <h1>Traduction d’aide</h1>
        <Notice tone="attention" title="Le texte français fait foi">
          {langs.data?.mention_fr ?? "Traduction d’aide produite automatiquement. Seul le texte original en français fait foi."} Un bail, un état des lieux ou une quittance traduit n’a aucune valeur contractuelle.
        </Notice>
        {langs.data ? (
          <p className="row small">
            Service : <span className="mono">{langs.data.service.name}</span> <Mode mode={langs.data.service.mode} />
            {langs.data.service.mode !== "production" ? <span className="muted">aucune traduction réelle : le texte reste en français.</span> : null}
          </p>
        ) : null}
        <div className="grid-2">
          <Card title={documentId ? "Traduire un document" : messageId ? "Traduire un message" : "Traduire un texte"}>
            <div className="stack-sm">
              <ErrorBox error={action.error && !fieldError(action.error, "text") ? action.error : null} />
              {documentId || messageId ? (
                <p className="small">Le texte sera lu depuis {documentId ? "le document" : "le message"} choisi, si vous y avez accès. Les documents scannés ne sont pas lus.</p>
              ) : (
                <Textarea label="Texte en français" rows={8} value={text} error={fieldError(action.error, "text")} onChange={(e) => setText(e.target.value)} />
              )}
              <Select label="Traduire vers" value={target} options={(langs.data?.items ?? []).map((l: any) => ({ value: l.code, label: l.label }))} onChange={(e) => setTarget(e.target.value)} />
              <Button busy={action.busy} onClick={translate}>
                Traduire
              </Button>
            </div>
          </Card>
          <Card title="Langue préférée">
            <Select
              label="Langue de vos échanges"
              value={user?.locale ?? "fr"}
              options={[
                { value: "fr", label: "Français" },
                { value: "en", label: "English" },
              ]}
              help="Utilisée pour vous proposer la traduction d’aide. Les documents officiels restent en français."
              onChange={(e) => setLocale(e.target.value)}
            />
          </Card>
        </div>
        {result ? (
          <Card title={`Traduction (${result.target_label})`} actions={<Mode mode={result.simulated ? "simulator" : "production"} />}>
            <div className="stack-sm">
              <p className="small">
                <strong>{result.mention}</strong> {result.mention_fr}
              </p>
              {result.note ? <Notice tone="simulateur">{result.note}</Notice> : null}
              {result.truncated ? <Notice tone="attention">Texte long : seul le début a été traduit.</Notice> : null}
              <div className="plateforme-texte-long">{result.translation}</div>
              <details>
                <summary className="small">Texte original en français (fait foi)</summary>
                <div className="plateforme-texte-long plateforme-original">{result.original}</div>
              </details>
            </div>
          </Card>
        ) : null}
        <Card title="Mes traductions récentes">
          {history.data?.items?.length ? (
            <ul className="list-plain small">
              {history.data.items.map((t: any) => (
                <li key={t.id}>
                  {dateTime(t.created_at)} · {t.source_type} · {t.target_lang} · {t.source_chars} caractères <Mode mode={t.mode === "simulateur" ? "simulator" : t.mode} />
                </li>
              ))}
            </ul>
          ) : (
            <Empty title="Aucune traduction." />
          )}
        </Card>
      </div>
    </PublicShell>
  );
}
